/**
 * Org Campaign Wizard
 *
 * Email-only campaign composer for corporate org dashboards.
 * 4 steps: pick event → pick recipients → compose → review & send.
 *
 * Data sources:
 *  - Events: /api/organizations/:id/dashboard  (existing endpoint, returns forms)
 *  - Contacts: /api/organizations/:id/contacts  (existing endpoint)
 *  - Sender status: /api/organizations/:id/gmail/status
 *  - Send: /api/organizations/:id/send-test (skipCredit=true for testing)
 *    Real batch send endpoint added separately.
 */

window.OrgCampaign = (function () {
  const state = {
    step: 1,
    events: [],
    contacts: [],
    selectedContacts: new Set(),
    tagFilter: '',
    searchQuery: '',
    event: null,
    subject: '',
    body: '',
    senderPref: 'auto',
    includeQRCode: false, // attach each registered guest's check-in QR (real-event campaigns only)
    recipientSource: 'contacts', // 'contacts' (CRM invite) | 'guests' (reminder to the event's registrants)
    eventGuests: null,           // resolved registered guests [{name,email}] for the selected event
    selectedGuests: new Set(),   // emails of the registered guests actually being sent to (task #626)
    eventGuestsFor: null,        // event id the guest list was loaded for (invalidates on event change)
    // Search + tag filter over the guest list, mirroring the contacts pane
    // (task #626 round 2). Deliberately NOT saved with the draft: a filter is a
    // way of looking at the list, not part of the campaign — the ticks are what
    // travel, and restoring a draft into a narrowed view would hide people who
    // are going to be sent to.
    guestSearchQuery: '',
    guestTagFilter: '',
    gmailStatus: null,
    org: null,          // fetched fresh when wizard opens — source of truth for name/slug
    sending: false
  };

  function _esc(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function _validHex(hex) {
    return typeof hex === 'string' && /^#[0-9a-fA-F]{6}$/.test(hex);
  }

  function _activeOrg() {
    return state.org || window.OrgDashboard?.currentOrg || {};
  }

  function _brandColor() {
    const c = _activeOrg()?.settings?.branding?.primaryColor;
    return _validHex(c) ? c : '#6b4f9c';
  }

  function _orgLogo() {
    return _activeOrg()?.logo || null;
  }

  function _textOn(hex) {
    const n = parseInt(hex.slice(1), 16);
    const toLin = (c) => (c /= 255) <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const L = 0.2126 * toLin((n >> 16) & 255) + 0.7152 * toLin((n >> 8) & 255) + 0.0722 * toLin(n & 255);
    return L > 0.55 ? '#0a0618' : '#ffffff';
  }

  function _darkenHex(hex, amount) {
    const n = parseInt(hex.slice(1), 16);
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
    const r = clamp(((n >> 16) & 255) - 255 * amount);
    const g = clamp(((n >> 8) & 255) - 255 * amount);
    const b = clamp((n & 255) - 255 * amount);
    return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
  }

  function _mixHex(a, b, weight) {
    const na = parseInt(a.slice(1), 16);
    const nb = parseInt(b.slice(1), 16);
    const w = Math.max(0, Math.min(1, weight));
    const mix = (sa, sb) => Math.round(sa * (1 - w) + sb * w);
    const r = mix((na >> 16) & 255, (nb >> 16) & 255);
    const g = mix((na >> 8) & 255, (nb >> 8) & 255);
    const bl = mix(na & 255, nb & 255);
    return '#' + [r, g, bl].map(v => v.toString(16).padStart(2, '0')).join('');
  }

  // Mirrors OrganizationService._senderLocalPart on the server.
  function _senderLocal(org) {
    const clean = (s) => String(s || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32);
    const fromName = clean(org?.name);
    if (fromName && fromName.length >= 2) return fromName;
    const fromSlug = clean(org?.slug);
    if (fromSlug && fromSlug.length >= 2) return fromSlug;
    return 'events';
  }

  function _orgSenderAddress() {
    const org = state.org || window.OrgDashboard?.currentOrg || {};
    return `${_senderLocal(org)}@events.quikrsvp.com`;
  }

  function _senderDisplayName() {
    const org = state.org || window.OrgDashboard?.currentOrg || {};
    return org.settings?.campaignSenderName || org.name || 'your organization';
  }

  function _showToast(msg, type = 'success') {
    if (window.OrgDashboard?._showToast) window.OrgDashboard._showToast(msg, type);
    else console.log(`[${type}] ${msg}`);
  }

  function _draftKey() {
    const orgId = window.OrgContext?.getCurrentOrgId?.() || 'unknown';
    return `orgCampaignDraft:${orgId}`;
  }

  function _draftPayload() {
    const subjectEl = document.getElementById('campaignSubject');
    const bodyEl = document.getElementById('campaignBody');
    const subject = subjectEl ? subjectEl.value : state.subject;
    const body = bodyEl ? bodyEl.value : state.body;
    state.subject = subject;
    state.body = body;
    return {
      step: state.step,
      event: state.event,
      selectedContacts: [...state.selectedContacts],
      selectedGuests: [...state.selectedGuests],
      // The recipient MODE has to travel with the recipient SELECTION — restored
      // guest ticks are invisible (and unsent) if the wizard reopens in contacts
      // mode. Same for the QR opt-in: a per-campaign choice, not a per-org one.
      recipientSource: state.recipientSource,
      includeQRCode: state.includeQRCode,
      tagFilter: state.tagFilter,
      searchQuery: state.searchQuery,
      subject,
      body,
      senderPref: state.senderPref
    };
  }

  // True once the server has it; false when only this browser kept a copy.
  async function _saveDraft(silent = false) {
    const payload = _draftPayload();
    // Always write localStorage as a fast local backup
    try {
      localStorage.setItem(_draftKey(), JSON.stringify({ ...payload, savedAt: Date.now() }));
    } catch {}
    // Persist to server so drafts survive across devices/sessions
    try {
      const res = await window.OrgContext.orgFetch('/campaigns/draft', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      state.lastSavedAt = res.savedAt || new Date().toISOString();
      _updateDraftStatus('Saved just now');
      if (!silent) _showToast('Draft saved', 'success');
      return true;
    } catch (err) {
      console.warn('Server draft save failed, local copy kept:', err);
      state.lastSavedAt = new Date().toISOString();
      _updateDraftStatus('Saved locally (offline)');
      _showToast('Saved in this browser only — server sync failed: ' + err.message, 'warning');
      return false;
    }
  }

  async function _loadDraft() {
    // Try server first, fall back to localStorage
    try {
      const res = await window.OrgContext.orgFetch('/campaigns/draft');
      if (res?.draft) return { ...res.draft, _source: 'server' };
    } catch (err) {
      console.warn('Could not load server draft:', err);
    }
    try {
      const raw = localStorage.getItem(_draftKey());
      if (!raw) return null;
      return { ...JSON.parse(raw), _source: 'local' };
    } catch { return null; }
  }

  async function _clearDraft() {
    try { localStorage.removeItem(_draftKey()); } catch {}
    try { await window.OrgContext.orgFetch('/campaigns/draft', { method: 'DELETE' }); } catch {}
  }

  function _updateDraftStatus(text) {
    const el = document.getElementById('campaignDraftStatus');
    if (el) el.textContent = text || '';
  }

  async function open(opts = {}) {
    // Reset state
    state.step = 1;
    state.events = [];
    state.contacts = [];
    state.selectedContacts = new Set();
    state.selectedGuests = new Set();
    state.tagFilter = '';
    state.searchQuery = '';
    state.guestTagFilter = '';
    state.guestSearchQuery = '';
    // The filter boxes are static page markup, so they survive a close — clear
    // what is IN them too, or the next campaign opens pre-narrowed by a search
    // the host typed last time (task #626 round 2).
    const guestSearchEl = document.getElementById('campaignGuestSearch');
    if (guestSearchEl) guestSearchEl.value = '';
    const contactSearchEl = document.getElementById('campaignContactSearch');
    if (contactSearchEl) contactSearchEl.value = '';
    state.event = opts.preselectedEventId || null;
    state.subject = '';
    state.body = '';
    state.senderPref = 'auto';
    state.recipientSource = opts.recipientSource === 'guests' ? 'guests' : 'contacts';
    state.eventGuests = null;
    state.eventGuestsFor = null;
    // Which event's guest SELECTION we are holding. Cleared with the rest, so
    // a fresh campaign starts with everyone ticked again (task #626).
    state.eventGuestsSelectedFor = null;
    state.includeQRCode = false;
    // Until the host touches the QR switch, it follows the journey: on for an
    // update to registered guests, off otherwise (task #1114).
    state.qrTouched = false;
    state.sending = false;
    // A new session starts clean: the last campaign's "Test email sent to…",
    // a compose error and the opt-in warning are not about this one (#1111).
    const testStatusEl = document.getElementById('campaignTestEmailStatus');
    if (testStatusEl) { testStatusEl.textContent = ''; testStatusEl.className = 'small mt-2'; }
    _clearComposeError();
    _clearRecipientsError();
    _setConsentError(false);
    _resetSaveDraftBtn();
    // Event-specific entry: show the "Invite to RSVP vs Event Updates" chooser
    // first, bypassing the generic event-selection list. The global "Email
    // contacts" button opens without this, keeping the full event picker.
    state.chooser = !!opts.chooser;
    state.chooserEventName = opts.eventName || '';

    // Show modal immediately so user sees something
    const modalEl = document.getElementById('orgCampaignModal');
    // getOrCreateInstance: "Continue" on the saved-draft note reopens it in
    // place (task #1112), and a second instance would stack a second backdrop.
    const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
    if (!modalEl.classList.contains('show')) modal.show();
    _updateDraftStatus('');
    // Reset any chooser state left over from a previous (possibly cancelled)
    // event-specific session so the steps/panes/footer are clean on every open.
    _hideChooser();
    // The FIRST paint must already be the view this entry point opens on. The
    // draft load below is awaited, so whatever is rendered here is on screen
    // for that whole round trip: an event row's Send opened on the full "Which
    // event is this about?" list and only then turned into its chooser
    // (task #940). The same for "Remind guests", which lands on step 2.
    if (opts.recipientSource === 'guests' && opts.preselectedEventId) state.step = 2;
    _renderStep();
    if (state.chooser) _showChooser(state.chooserEventName);

    // Hydrate from the saved draft only when the host asked to continue it
    // (task #1112): its row on the Outreach tab has "Continue draft". Every
    // campaign used to open on whatever draft had been saved last, with no
    // sign of where it came from.
    const draft = opts.resumeDraft ? await _loadDraft() : null;
    if (draft) {
      state.step = draft.step || 1;
      state.event = draft.event || state.event || null;
      // A draft goes back to its own journey: past the chooser, on its step.
      state.chooser = false;
      _hideChooser();
      state.selectedContacts = new Set(draft.selectedContacts || []);
      state.selectedGuests = new Set(draft.selectedGuests || []);
      // A restored draft carries its own ticks — don't let the first render
      // overwrite them with "everyone". The marker stays DERIVED rather than
      // stored, but it has to name the event the ticks were made for, which is
      // the draft's event. Using state.event would be wrong when the caller
      // preselected a different one (opts.preselectedEventId wins above): the
      // stale ticks would be treated as that event's selection instead of being
      // replaced by its own "everyone" default on first render.
      if (draft.selectedGuests) state.eventGuestsSelectedFor = draft.event || state.event;
      // An explicit entry point ("Remind guests") states the mode; otherwise the
      // draft's own mode decides which recipient UI the host comes back to.
      if (!opts.recipientSource && draft.recipientSource) state.recipientSource = draft.recipientSource;
      state.includeQRCode = !!draft.includeQRCode;
      state.qrTouched = true;   // the draft's own choice, kept as saved
      state.tagFilter = draft.tagFilter || '';
      state.searchQuery = draft.searchQuery || '';
      state.subject = draft.subject || '';
      state.body = draft.body || '';
      state.senderPref = draft.senderPref || 'auto';
      state.lastSavedAt = draft.savedAt || null;

      document.getElementById('campaignSubject').value = state.subject;
      document.getElementById('campaignBody').value = state.body;
      const searchEl = document.getElementById('campaignContactSearch');
      if (searchEl) searchEl.value = state.searchQuery;
      _renderStep();

      const when = state.lastSavedAt ? _formatSaved(state.lastSavedAt) : '';
      _updateDraftStatus(`Draft${when ? ' · ' + when : ''}`);
    } else {
      document.getElementById('campaignSubject').value = '';
      document.getElementById('campaignBody').value = '';
      // A saved draft is not loaded into a new campaign, but it is not hidden
      // either: the footer says it is there, with Continue (task #1112).
      if (!opts.resumeDraft) {
        _loadDraft().then((d) => {
          if (!d) return;
          const el = document.getElementById('campaignDraftStatus');
          if (!el || el.textContent) return;
          const when = d.savedAt ? _formatSaved(d.savedAt) : '';
          el.innerHTML = `You have a saved draft${when ? ' (' + _esc(when.replace(/^Last saved /, '')) + ')' : ''} · <a href="#" onclick="OrgCampaign.open({ resumeDraft: true }); return false;">Continue it</a>`;
        }).catch(() => {});
      }
    }
    document.getElementById('campaignConsentCheckbox').checked = false;

    // "Remind guests" entry point: land directly on the recipients step with
    // the registered-guests source active so the guest list is front and center.
    if (opts.recipientSource === 'guests' && opts.preselectedEventId) {
      state.step = 2;
      _renderStep();
    }

    // Event-specific chooser takes over the modal until the host picks a journey.
    if (state.chooser) {
      _showChooser(state.chooserEventName);
    }

    // Load data in parallel — org fetch ensures we have the latest name/slug/settings
    Promise.all([
      _loadOrg(),
      _loadEvents(),
      _loadContacts(),
      _loadGmailStatus()
    ]);
  }

  async function _loadOrg() {
    try {
      const orgId = window.OrgContext?.getCurrentOrgId?.();
      if (!orgId) {
        // Fall back to cached currentOrg from OrgDashboard
        state.org = window.OrgDashboard?.currentOrg || null;
        return;
      }
      const res = await fetch(`/api/organizations/${orgId}`, {
        headers: { Authorization: `Bearer ${await window.firebaseAuth.currentUser.getIdToken()}` }
      });
      const data = await res.json();
      state.org = data.organization || window.OrgDashboard?.currentOrg || null;
    } catch (err) {
      console.warn('Could not load org info:', err);
      state.org = window.OrgDashboard?.currentOrg || null;
    }
  }

  async function _loadEvents() {
    try {
      const data = await window.OrgContext.orgFetch('/dashboard');
      // Only filter out explicitly expired events (treat missing status as fine)
      state.events = (data.forms || []).filter(f => f.status !== 'expired');
      _renderEvents();
      // The recipient-source toggle depends on the loaded events list — if the
      // user is already on step 2 (deep-link / restored draft), refresh it now.
      if (state.step === 2) _renderRecipientSource();
      // A host fast enough to reach Compose first would otherwise not see the
      // QR switch, which is drawn from this list (task #1124).
      if (state.step === 3) _syncQrOption();
    } catch (err) {
      document.getElementById('campaignEventList').innerHTML =
        `<p class="panel-hint">Could not load events: ${_esc(err.message)}</p>`;
    }
  }

  async function _loadContacts() {
    try {
      const data = await window.OrgContext.orgFetch('/contacts');
      state.contacts = (data.contacts || []).filter(c => c.email);
      _populateTagFilter();
      _renderContacts();
    } catch (err) {
      document.getElementById('campaignContactList').innerHTML =
        `<p class="panel-hint">Could not load contacts: ${_esc(err.message)}</p>`;
    }
  }

  async function _loadGmailStatus() {
    try {
      state.gmailStatus = await window.OrgContext.orgFetch('/gmail/status');
    } catch (err) {
      state.gmailStatus = { connected: false };
    }
  }

  function _eventsPageUrl() {
    const org = state.org || window.OrgDashboard?.currentOrg || {};
    if (!org.slug) return null;
    return `${window.location.origin}/events/${org.slug}`;
  }

  function _renderEventsPageOption() {
    const option = document.getElementById('campaignEventsPageOption');
    const urlEl = document.getElementById('campaignEventsPageUrl');
    const url = _eventsPageUrl();
    if (option && urlEl && url) {
      option.style.display = '';
      urlEl.textContent = url.replace(/^https?:\/\//, '');
      const input = option.querySelector('input');
      input.checked = state.event === '__events-page__';
      if (!input.dataset.bound) {
        input.dataset.bound = '1';
        input.addEventListener('change', (e) => {
          if (e.target.checked) state.event = '__events-page__';
        });
      }
    }
  }

  function _renderEvents() {
    _renderEventsPageOption();
    const container = document.getElementById('campaignEventList');
    if (!container) return;
    if (state.events.length === 0) {
      container.innerHTML = '<p class="panel-hint">No specific events yet. You can still send a message or link to your events page.</p>';
    } else {
      container.innerHTML = state.events.map(e => `
        <label class="campaign-event-row">
          <input type="radio" name="campaignEvent" value="${_esc(e.id)}" ${state.event === e.id ? 'checked' : ''}>
          <div class="campaign-event-info">
            <div class="campaign-event-title">${_esc(e.title)}</div>
            <div class="campaign-event-meta">
              ${e.eventDate ? '<i class="bi bi-calendar3"></i> ' + _esc(e.eventDate) : ''}
              ${e.responseCount != null ? ` \u00B7 ${e.responseCount} responses` : ''}
            </div>
          </div>
        </label>
      `).join('');

      container.querySelectorAll('input[name="campaignEvent"]').forEach(r => {
        r.addEventListener('change', (e) => { state.event = e.target.value || null; });
      });
    }

    const noneInput = document.querySelector('.campaign-event-row.none input');
    if (noneInput) {
      noneInput.checked = !state.event;
      if (!noneInput.dataset.bound) {
        noneInput.dataset.bound = '1';
        noneInput.addEventListener('change', (e) => {
          if (e.target.checked) state.event = null;
        });
      }
    }
  }

  function _populateTagFilter() {
    const sel = document.getElementById('campaignContactTagFilter');
    if (!sel) return;
    const tags = new Set();
    state.contacts.forEach(c => (c.tags || []).forEach(t => tags.add(t)));
    sel.innerHTML = '<option value="">All tags</option>' +
      [...tags].sort().map(t => `<option value="${_esc(t)}">${_esc(t)}</option>`).join('');
    sel.onchange = () => { state.tagFilter = sel.value; _renderContacts(); };
  }

  function _filteredContacts() {
    const q = state.searchQuery.toLowerCase().trim();
    return state.contacts.filter(c => {
      if (state.tagFilter && !(c.tags || []).includes(state.tagFilter)) return false;
      if (q) {
        const hay = `${c.name || ''} ${c.email || ''} ${c.company || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }

  function _renderContacts() {
    const container = document.getElementById('campaignContactList');
    if (!container) return;

    const search = document.getElementById('campaignContactSearch');
    if (search && !search.oninput) {
      search.oninput = () => { state.searchQuery = search.value; _renderContacts(); };
    }

    const filtered = _filteredContacts();

    if (state.contacts.length === 0) {
      container.innerHTML = `<p class="panel-hint">No contacts in your CRM yet. <a href="#" onclick="document.querySelector('[data-tab=\\'contacts\\']')?.click(); bootstrap.Modal.getInstance(document.getElementById('orgCampaignModal'))?.hide(); return false;">Add contacts</a> first.</p>`;
      return;
    }

    if (filtered.length === 0) {
      container.innerHTML = '<p class="panel-hint">No contacts match these filters.</p>';
      _updateSelectedCount();
      return;
    }

    container.innerHTML = filtered.map(c => `
      <label class="campaign-contact-row">
        <input type="checkbox" class="campaign-contact-check" value="${_esc(c.id)}" ${state.selectedContacts.has(c.id) ? 'checked' : ''}>
        <div class="campaign-contact-info">
          <div class="campaign-contact-name">${_esc(c.name || c.email)}</div>
          <div class="campaign-contact-meta">
            ${_esc(c.email)}${c.company ? ' \u00B7 ' + _esc(c.company) : ''}
            ${(c.tags || []).length ? ' \u00B7 ' + (c.tags || []).slice(0, 3).map(t => `<span class="campaign-contact-tag">${_esc(t)}</span>`).join(' ') : ''}
          </div>
        </div>
      </label>
    `).join('');

    container.querySelectorAll('.campaign-contact-check').forEach(cb => {
      cb.addEventListener('change', (e) => {
        if (e.target.checked) state.selectedContacts.add(e.target.value);
        else state.selectedContacts.delete(e.target.value);
        _updateSelectedCount();
      });
    });

    _updateSelectedCount();
  }

  function _updateSelectedCount() {
    const el = document.getElementById('campaignSelectedCount');
    if (el) el.textContent = state.selectedContacts.size;
    if (state.selectedContacts.size) _clearRecipientsError();
  }

  function toggleAll(checked) {
    const visible = _filteredContacts();
    visible.forEach(c => {
      if (checked) state.selectedContacts.add(c.id);
      else state.selectedContacts.delete(c.id);
    });
    _renderContacts();
  }

  // ---- Recipient source: CRM contacts vs the event's registered guests ----
  // "Registered guests" turns the campaign into a reminder/follow-up to
  // everyone who already RSVP'd (yes/maybe) to the selected event — the
  // audience that pairs with the per-guest check-in QR option on Send.
  function _isRealEventSelected() {
    return !!state.event && state.event !== '__events-page__' &&
      state.events.some(e => e.id === state.event);
  }

  function _renderRecipientSource() {
    const wrap = document.getElementById('campaignRecipientSourceWrap');
    const contactsUI = document.getElementById('campaignContactsUI');
    const guestsUI = document.getElementById('campaignGuestsUI');
    if (!wrap || !contactsUI || !guestsUI) return;

    const realEvent = _isRealEventSelected();
    // An event id that simply hasn't finished loading into state.events yet still
    // "looks real" — don't reset a chosen 'guests' source merely because the events
    // list is mid-load (the chooser / deep-link sets the source before events load).
    const eventLooksReal = !!state.event && state.event !== '__events-page__';
    wrap.style.display = realEvent ? '' : 'none';
    if (!eventLooksReal) state.recipientSource = 'contacts';
    // Changing the event invalidates a previously loaded guest list
    if (state.eventGuestsFor && state.eventGuestsFor !== state.event) {
      state.eventGuests = null;
      state.eventGuestsFor = null;
    }

    wrap.querySelectorAll('input[name="campaignRecipientSource"]').forEach(r => {
      r.checked = r.value === state.recipientSource;
      if (!r.dataset.bound) {
        r.dataset.bound = '1';
        r.addEventListener('change', () => {
          if (!r.checked) return;
          state.recipientSource = r.value;
          _clearRecipientsError();
          _renderRecipientSource();
        });
      }
    });
    wrap.querySelectorAll('.campaign-sender-option').forEach(l =>
      l.classList.toggle('selected', l.querySelector('input').checked));

    const useGuests = realEvent && state.recipientSource === 'guests';
    contactsUI.style.display = useGuests ? 'none' : '';
    guestsUI.style.display = useGuests ? '' : 'none';
    if (useGuests && (!state.eventGuests || state.eventGuestsFor !== state.event)) {
      _loadEventGuests();
    } else if (useGuests) {
      _renderEventGuests();
    }
  }

  async function _loadEventGuests() {
    const list = document.getElementById('campaignGuestList');
    const hint = document.getElementById('campaignGuestsHint');
    if (list) list.innerHTML = '<div class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>';
    if (hint) hint.textContent = 'Loading registered guests…';
    try {
      const token = await window.firebaseAuth.currentUser.getIdToken();
      const r = await fetch(`/api/forms/${encodeURIComponent(state.event)}/responses?limit=500`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('could not load responses');
      const data = await r.json();
      const seen = new Set();
      state.eventGuests = (data.responses || [])
        .filter(resp => {
          const att = String(resp.attending || '').toLowerCase();
          return (att === 'yes' || att === 'attending' || att === 'maybe') && (resp.email || '').trim();
        })
        .map(resp => ({ name: resp.guestName || resp.name || '', email: String(resp.email).toLowerCase().trim() }))
        .filter(g => !seen.has(g.email) && seen.add(g.email));
      state.eventGuestsFor = state.event;
    } catch (err) {
      state.eventGuests = null;
      state.eventGuestsFor = null;
      if (list) list.innerHTML = `<p class="panel-hint">Could not load guests: ${_esc(err.message)}</p>`;
      if (hint) hint.textContent = '';
      return;
    }
    _renderEventGuests();
  }

  /**
   * A registered guest is an RSVP, not a CRM record, so it has no tags of its
   * own. But the same person is very often already one of your contacts — so
   * match on email and carry their company and tags across (task #626 round 2,
   * "it should also have the search box and tags").
   *
   * Done at render time rather than load time: the guest list and the contact
   * list arrive from two different requests, and whichever lands second must
   * still see the other.
   */
  function _guestsWithContactDetails() {
    const byEmail = new Map();
    (state.contacts || []).forEach(c => {
      const e = String(c.email || '').toLowerCase().trim();
      if (e && !byEmail.has(e)) byEmail.set(e, c);
    });
    return (state.eventGuests || []).map(g => {
      const c = byEmail.get(g.email);
      return c ? { ...g, company: c.company || '', tags: Array.isArray(c.tags) ? c.tags : [] } : g;
    });
  }

  function _filteredGuests() {
    const q = (state.guestSearchQuery || '').toLowerCase().trim();
    return _guestsWithContactDetails().filter(g => {
      if (state.guestTagFilter && !(g.tags || []).includes(state.guestTagFilter)) return false;
      if (q) {
        const hay = `${g.name || ''} ${g.email || ''} ${g.company || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }

  function _populateGuestTagFilter() {
    const sel = document.getElementById('campaignGuestTagFilter');
    if (!sel) return;
    const tags = new Set();
    _guestsWithContactDetails().forEach(g => (g.tags || []).forEach(t => tags.add(t)));
    // A tag that is no longer on anyone in this list would filter to nothing —
    // drop the selection rather than showing an empty list with no explanation.
    if (state.guestTagFilter && !tags.has(state.guestTagFilter)) state.guestTagFilter = '';
    sel.innerHTML = '<option value="">All tags</option>' +
      [...tags].sort().map(t => `<option value="${_esc(t)}"${t === state.guestTagFilter ? ' selected' : ''}>${_esc(t)}</option>`).join('');
    // Only offer the dropdown when there is something to choose: none of these
    // guests being contacts is the common case on a wedding.
    sel.style.display = tags.size ? '' : 'none';
    sel.onchange = () => { state.guestTagFilter = sel.value; _renderEventGuests(); };
  }

  function _renderEventGuests() {
    const list = document.getElementById('campaignGuestList');
    const hint = document.getElementById('campaignGuestsHint');
    const header = document.getElementById('campaignGuestsHeader');
    const guests = state.eventGuests || [];
    const eventTitle = state.events.find(e => e.id === state.event)?.title || 'this event';

    // Everyone is selected the first time the list is seen — that is what this
    // audience meant before it was selectable, so the default send is unchanged
    // (task #626). Anyone who has since left the list drops out of the set.
    const emails = new Set(guests.map(g => g.email));
    let dropped = 0;
    [...state.selectedGuests].forEach(e => {
      if (!emails.has(e)) { state.selectedGuests.delete(e); dropped++; }
    });
    if (state.eventGuestsSelectedFor !== state.event) {
      state.selectedGuests = new Set(emails);
      state.eventGuestsSelectedFor = state.event;
      dropped = 0; // falling back to "everyone" — nothing was lost to report
    }

    const filterRow = document.getElementById('campaignGuestFilterRow');
    if (filterRow) filterRow.style.display = guests.length ? '' : 'none';
    if (header) header.style.display = guests.length ? '' : 'none';
    if (hint) {
      // A restored draft can name guests who have since left the list (RSVP
      // changed, response deleted). They are dropped rather than sent to — but
      // say so, or the host just sees a selection quietly smaller than they left
      // it, with no clue why.
      const staleNote = dropped
        ? ` <span class="text-warning"><i class="bi bi-exclamation-triangle me-1"></i>${dropped} guest${dropped === 1 ? '' : 's'} from your saved draft ${dropped === 1 ? 'is' : 'are'} no longer on this list, so ${dropped === 1 ? 'it was' : 'they were'} unticked.</span>`
        : '';
      hint.innerHTML = guests.length
        ? `<i class="bi bi-people me-1"></i><strong>${guests.length}</strong> registered guest${guests.length === 1 ? '' : 's'} of <strong>${_esc(eventTitle)}</strong> (everyone who RSVP'd yes/maybe and left an email). Untick anyone who should not get this.${staleNote}`
        : '';
    }
    if (!list) return;
    if (guests.length === 0) {
      list.innerHTML = `<p class="panel-hint">No registered guests with an email address yet for <strong>${_esc(eventTitle)}</strong>. Guests appear here once they RSVP.</p>`;
      _updateGuestSelectedCount();
      return;
    }

    const search = document.getElementById('campaignGuestSearch');
    if (search && !search.oninput) {
      search.oninput = () => { state.guestSearchQuery = search.value; _renderEventGuests(); };
    }
    _populateGuestTagFilter();

    const filtered = _filteredGuests();
    if (filtered.length === 0) {
      list.innerHTML = '<p class="panel-hint">No registered guests match these filters.</p>';
      _updateGuestSelectedCount();
      return;
    }

    list.innerHTML = filtered.map(g => `
      <label class="campaign-contact-row">
        <input type="checkbox" class="campaign-guest-check" value="${_esc(g.email)}" ${state.selectedGuests.has(g.email) ? 'checked' : ''}>
        <div class="campaign-contact-info">
          <div class="campaign-contact-name">${_esc(g.name || g.email)}</div>
          <div class="campaign-contact-meta">
            ${_esc(g.email)}${g.company ? ' · ' + _esc(g.company) : ''}
            ${(g.tags || []).length ? ' · ' + (g.tags || []).slice(0, 3).map(t => `<span class="campaign-contact-tag">${_esc(t)}</span>`).join(' ') : ''}
          </div>
        </div>
      </label>
    `).join('');

    list.querySelectorAll('.campaign-guest-check').forEach(cb => {
      cb.addEventListener('change', (e) => {
        if (e.target.checked) state.selectedGuests.add(e.target.value);
        else state.selectedGuests.delete(e.target.value);
        _updateGuestSelectedCount();
      });
    });
    _updateGuestSelectedCount();
  }

  function _updateGuestSelectedCount() {
    const el = document.getElementById('campaignGuestSelectedCount');
    // The counter is the whole audience, not the filtered view — a search that
    // hides someone must not read as having dropped them from the send.
    if (el) el.textContent = state.selectedGuests.size;
    if (state.selectedGuests.size) _clearRecipientsError();
    const all = document.getElementById('campaignGuestSelectAll');
    // The box says "select all VISIBLE", so it has to reflect the visible set:
    // against the whole list it would sit unticked while everything on screen
    // was ticked (task #626 round 2).
    const visible = _filteredGuests();
    const chosen = visible.filter(g => state.selectedGuests.has(g.email)).length;
    if (all) {
      all.checked = visible.length > 0 && chosen === visible.length;
      all.indeterminate = chosen > 0 && chosen < visible.length;
    }
  }

  function toggleAllGuests(checked) {
    // Visible only — with a filter on, "select all visible" that reached the
    // whole list would silently add people the host had filtered away.
    _filteredGuests().forEach(g => {
      if (checked) state.selectedGuests.add(g.email);
      else state.selectedGuests.delete(g.email);
    });
    _renderEventGuests();
  }

  function _renderSenderOptions() {
    const container = document.getElementById('campaignSenderOptions');
    if (!container) return;
    // Every mailbox the org has connected, plus the QuikRSVP fallback that is
    // always there (task #628). The old shape reported a single account; a
    // status payload without the list is treated as a list of one so an older
    // cached response still renders.
    const accounts = Array.isArray(state.gmailStatus?.accounts)
      ? state.gmailStatus.accounts
      : (state.gmailStatus?.connected
        ? [{ id: 'gmail', email: state.gmailStatus.email, name: state.gmailStatus.name,
             sentToday: state.gmailStatus.sentToday, dailyQuota: state.gmailStatus.dailyQuota,
             isDefault: true }]
        : []);
    const connected = accounts.length > 0;
    const defaultId = state.gmailStatus?.defaultSender
      || (accounts.find(a => a.isDefault) || accounts[0] || {}).id
      || 'quikrsvp';

    // Normalize the selection:
    //  • nothing connected → the fallback is the only option
    //  • the legacy 'auto'/'gmail' → whichever account is the org's default
    //  • an id that is no longer connected (the mailbox was disconnected while
    //    this draft sat open) → back to the default, rather than sending from
    //    an address that isn't there any more
    if (!connected) {
      state.senderPref = 'quikrsvp';
    } else if (state.senderPref === 'auto' || state.senderPref === 'gmail' || !state.senderPref) {
      state.senderPref = defaultId;
    } else if (state.senderPref !== 'quikrsvp' && !accounts.some(a => a.id === state.senderPref)) {
      state.senderPref = defaultId;
    }

    const gmailOption = accounts.map(a => `
      <label class="campaign-sender-option ${state.senderPref === a.id ? 'selected' : ''}">
        <input type="radio" name="campaignSender" value="${_esc(a.id)}" ${state.senderPref === a.id ? 'checked' : ''}>
        <div>
          <strong>${_esc(a.name || 'Gmail')}${a.id === defaultId ? ' <span class="sender-option-badge">Default</span>' : ''}</strong>
          <span>From ${_esc(a.email)} \u00B7 ${a.sentToday || 0}/${a.dailyQuota || 2000} sent today</span>
        </div>
      </label>`).join('');

    container.innerHTML = `
      ${gmailOption}
      <label class="campaign-sender-option ${state.senderPref === 'quikrsvp' ? 'selected' : ''}">
        <input type="radio" name="campaignSender" value="quikrsvp" ${state.senderPref === 'quikrsvp' ? 'checked' : ''}>
        <div>
          <strong>QuikRSVP${!connected ? '' : ' (fallback)'}${defaultId === 'quikrsvp' && connected ? ' <span class="sender-option-badge">Default</span>' : ''}</strong>
          <span>Sends as <b>${_esc(_senderDisplayName())}</b> from <code>${_esc(_orgSenderAddress())}</code></span>
        </div>
      </label>
      ${!connected ? `
        <p class="panel-hint mt-2" style="font-size: 0.8rem;">
          <i class="bi bi-info-circle me-1"></i>
          Want emails to come from your own Gmail address? <a href="#" onclick="bootstrap.Modal.getInstance(document.getElementById('orgCampaignModal'))?.hide(); document.querySelector('[data-tab=\\'settings\\']')?.click(); return false;">Connect Gmail in Settings</a>.
        </p>` : ''}
    `;

    container.querySelectorAll('input[name="campaignSender"]').forEach(r => {
      r.addEventListener('change', (e) => {
        state.senderPref = e.target.value;
        container.querySelectorAll('.campaign-sender-option').forEach(l => l.classList.toggle('selected', l.querySelector('input').checked));
      });
    });
  }

  function _renderReview() {
    const event = state.events.find(e => e.id === state.event);
    const useGuests = _isRealEventSelected() && state.recipientSource === 'guests';
    const recipientCount = useGuests ? state.selectedGuests.size : state.selectedContacts.size;
    document.getElementById('reviewEvent').textContent = event ? event.title : 'No event (message only)';
    document.getElementById('reviewRecipients').textContent = useGuests
      ? `${recipientCount} registered guest${recipientCount === 1 ? '' : 's'}`
      : `${recipientCount} contact${recipientCount === 1 ? '' : 's'}`;
    document.getElementById('reviewSubject').textContent = state.subject || '(none)';
    // What this send uses, against the emails left this month (task #1019:
    // it said "of 100 included" - a number the plan never had; /corporate
    // sells 500, then credits).
    const creditsEl = document.getElementById('reviewCredits');
    creditsEl.textContent = `${recipientCount} email${recipientCount === 1 ? '' : 's'}`;
    window.OrgContext.orgFetch('/send-stats').then((r) => {
      const st = r && r.stats;
      if (!st || !Number.isFinite(st.allowanceLimit)) return;
      const left = Math.max(0, st.allowanceLimit - (st.allowanceUsedThisMonth || 0));
      const over = Math.max(0, recipientCount - left);
      creditsEl.textContent = `${recipientCount} email${recipientCount === 1 ? '' : 's'} · ${left.toLocaleString()} of ${st.allowanceLimit.toLocaleString()} left this month`
        + (over ? ` · ${over.toLocaleString()} from credits` : '');
    }).catch(() => {});
  }

  /**
   * The check-in QR option, on the COMPOSE step where the message is written
   * (task #639).
   *
   * It used to sit on the final screen, one click before sending — a control
   * that changes what the email CONTAINS, offered at the moment there is no
   * longer anywhere to see what it did. It now sits above the live preview, and
   * the preview draws the block it adds.
   *
   * Only for a real event: the code is a guest's own registration, so an
   * "events page" or message-only campaign has nothing to put in it.
   */
  //
  // Only for an update to the event's registered guests (task #1111): a code
  // is a guest's own registration, and an invite goes to contacts who have
  // not registered yet, so it attached nothing for most of them. Until the
  // host touches it, it starts as this event's last Event Update was sent,
  // or, before the first one, on when the event has QR check-in on and off
  // when it does not (task #1124; it was always on, #1114).
  function _qrDefaultFor(event) {
    if (typeof event.campaignQrDefault === 'boolean') return event.campaignQrDefault;
    return event.enableCheckin === true;
  }

  function _syncQrOption() {
    const qrWrap = document.getElementById('campaignQrWrap');
    if (!qrWrap) return;
    const event = state.events.find(e => e.id === state.event);
    const isRealEvent = !!event && state.event !== '__events-page__';
    const toGuests = isRealEvent && state.recipientSource === 'guests';
    qrWrap.style.display = toGuests ? '' : 'none';
    if (!toGuests) state.includeQRCode = false;
    else if (!state.qrTouched) state.includeQRCode = _qrDefaultFor(event);
    const qrCb = document.getElementById('campaignIncludeQR');
    if (!qrCb) return;
    qrCb.checked = state.includeQRCode;
    if (qrCb.dataset.bound) return;
    qrCb.dataset.bound = '1';
    qrCb.addEventListener('change', () => {
      state.includeQRCode = qrCb.checked;
      state.qrTouched = true;
      // Show it immediately — being able to see what the switch does is the
      // whole reason it moved here.
      _renderLivePreview();
    });
  }

  // `src` is the campaign to draw: the one being written by default, or a sent
  // one for its Preview on the Outreach tab (task #1115).
  function _renderEmailBody(contact, forEmail = false, src = null) {
    // Household-aware, like the send (task #908).
    const firstName = (typeof window !== 'undefined' && window.HouseholdNames
      ? window.HouseholdNames.invitationGreeting(contact || {})
      : (contact?.name || '').split(' ')[0]) || 'there';
    const name = contact?.name || contact?.email || '';
    const email = contact?.email || '';
    const c = src || {
      body: state.body,
      event: state.events.find(e => e.id === state.event),
      isEventsPage: state.event === '__events-page__',
      useGuests: _isRealEventSelected() && state.recipientSource === 'guests',
      includeQRCode: state.includeQRCode
    };
    const event = c.event;
    const eventsPageUrl = _eventsPageUrl();
    const isEventsPage = !!c.isEventsPage;
    const orgName = _senderDisplayName();

    let body = c.body || '';
    body = body.replace(/\{\{first_name\}\}/g, firstName)
               .replace(/\{\{name\}\}/g, name)
               .replace(/\{\{email\}\}/g, email);
    const escaped = _esc(body).replace(/\n/g, '<br>');

    // The same card the send builds (task #1116): registered guests get an
    // "Event reminder" with the date, times and venue and no button; an
    // invite gets the same details and RSVP here. The preview used to show
    // the invite card for both.
    let linkBlock = '';
    if (event) {
      const useGuests = !!c.useGuests;
      linkBlock = _linkCard({
        kicker: useGuests ? 'Event reminder' : 'You\u2019re invited',
        title: event.title,
        lines: window.EventCardDetails.eventCardLines(event),
        href: _esc(window.EventCardDetails.eventRsvpUrl('https://quikrsvp.com', event.id)),
        cta: useGuests ? '' : 'RSVP here'
      });
    } else if (isEventsPage && eventsPageUrl) {
      linkBlock = _linkCard({
        kicker: 'Upcoming events',
        title: `All events from ${orgName}`,
        href: _esc(eventsPageUrl),
        cta: 'Browse events'
      });
    }

    // The block the QR option adds, so the switch above the preview shows its
    // own effect (task #639). A real send mints each guest's own code; the
    // preview stands in for it, and says so, rather than inventing a scannable
    // square that would be the wrong person's.
    const qrBlock = (c.includeQRCode && event) ? _qrPreviewBlock(forEmail) : '';

    const bodyHtml = escaped || '<span style="color:#b9b4c2;font-style:italic;">Your message will appear here</span>';
    const brand = _brandColor();
    const brandDeep = _darkenHex(brand, 0.16);
    const brandSoft = _mixHex(brand, '#ffffff', 0.93);
    const brandBg = _mixHex(brand, '#ffffff', 0.97);
    const pageBg = _mixHex(brand, '#ffffff', 0.95);
    const onBrand = _textOn(brand);
    const logo = _orgLogo();

    const nameLabel = `<span style="display:inline-block;vertical-align:middle;font-size:13px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:${brandDeep};">${_esc(orgName)}</span>`;
    const headerInner = logo
      ? `<img src="${_esc(logo)}" alt="${_esc(orgName)}" style="height:36px;max-width:150px;vertical-align:middle;display:inline-block;margin-right:14px;">${nameLabel}`
      : nameLabel;

    return `
<div style="background:${pageBg};padding:24px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <div style="max-width:560px;margin:0 auto;background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #ece8f1;">
    <div style="padding:22px 32px;border-bottom:3px solid ${brand};">
      ${headerInner}
    </div>
    <div style="padding:32px;color:#1f1828;line-height:1.65;font-size:15px;">
      ${bodyHtml}
      ${linkBlock}
      ${qrBlock}
    </div>
    <div style="padding:18px 32px;border-top:1px solid #efeaf3;background:#fafafe;font-size:11.5px;color:#8a8194;letter-spacing:0.02em;">
      Sent by <strong style="color:#6b6474;">${_esc(orgName)}</strong> \u00B7 Powered by QuikRSVP
    </div>
  </div>
</div>`.trim();
  }

  /**
   * A stand-in for the guest's personal check-in code (task #639).
   *
   * Deliberately NOT a real QR: each guest's is minted per recipient at send
   * time, so anything drawn here would be either nobody's or the wrong
   * person's. What the host needs from the preview is WHERE it lands and how
   * much room it takes, and the caption says the rest.
   */
  // The stand-in is a grey, unscannable QR-like PNG (task #1110): the test
  // email is this same HTML, and an icon-font glyph is drawn by no email
  // client, so the box arrived as an empty white square. An email needs the
  // absolute address; the preview loads it from wherever the page is served.
  function _qrPreviewBlock(forEmail) {
    const brand = _brandColor();
    const brandDeep = _darkenHex(brand, 0.16);
    const brandBorder = _mixHex(brand, '#ffffff', 0.80);
    const brandBg = _mixHex(brand, '#ffffff', 0.95);
    const origin = forEmail || typeof location === 'undefined' ? 'https://quikrsvp.com' : location.origin;
    return `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top:24px;border-collapse:separate;">
  <tr><td align="center" style="padding:22px 24px;background:${brandBg};border:1px solid ${brandBorder};border-radius:10px;">
    <div style="font-size:11px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:${brandDeep};margin-bottom:12px;">Your check-in code</div>
    <img src="${origin}/assets/images/email-qr-placeholder.png" alt="Sample QR code" width="120" height="120" style="width:120px;height:120px;display:block;margin:0 auto;background:#ffffff;border:1px solid ${brandBorder};border-radius:8px;">
    <div style="font-size:12.5px;color:#6b6474;margin-top:12px;line-height:1.5;">Sample QR code. Each guest gets their own check-in code here when you send.</div>
  </td></tr>
</table>`.trim();
  }

  function _linkCard({ kicker, title, lines = [], href, cta }) {
    const brand = _brandColor();
    const brandDeep = _darkenHex(brand, 0.16);
    const brandBg = _mixHex(brand, '#ffffff', 0.95);
    const brandBorder = _mixHex(brand, '#ffffff', 0.80);
    const onBrand = _textOn(brand);
    return `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top:28px;border-collapse:separate;">
  <tr><td style="padding:22px 24px;background:${brandBg};border:1px solid ${brandBorder};border-radius:10px;">
    <div style="font-size:11px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:${brandDeep};margin-bottom:6px;">${_esc(kicker)}</div>
    <div style="font-size:17px;font-weight:600;color:#1f1828;line-height:1.35;">${_esc(title)}</div>
    ${lines.map(l => `<div style="font-size:13.5px;color:#6b6474;margin-top:4px;">${_esc(l)}</div>`).join('')}
    ${cta ? `<a href="${href}" style="display:inline-block;margin-top:16px;background:${brand};color:${onBrand};padding:11px 22px;border-radius:6px;text-decoration:none;font-weight:600;font-size:14px;letter-spacing:0.01em;">${_esc(cta)} &rarr;</a>` : ''}
  </td></tr>
</table>`.trim();
  }

  function _renderLivePreview() {
    const target = document.getElementById('campaignLivePreview');
    if (!target) return;
    _syncComposeFromDom();
    const firstId = [...state.selectedContacts][0];
    const sampleContact = state.contacts.find(c => c.id === firstId) || { name: 'Sample Recipient', email: 'sample@example.com' };
    target.innerHTML = _renderEmailBody(sampleContact);
  }

  function _renderStep() {
    // Step indicators — clickable to jump freely between steps
    document.querySelectorAll('.campaign-step').forEach(el => {
      const s = parseInt(el.dataset.step, 10);
      el.classList.toggle('active', s === state.step);
      el.classList.toggle('complete', s < state.step);
      if (!el.dataset.bound) {
        el.dataset.bound = '1';
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.addEventListener('click', () => goToStep(parseInt(el.dataset.step, 10)));
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToStep(parseInt(el.dataset.step, 10)); }
        });
      }
    });

    // Panes
    document.querySelectorAll('.campaign-pane').forEach(el => {
      el.classList.toggle('active', parseInt(el.dataset.pane, 10) === state.step);
    });

    // Step-specific rendering
    if (state.step === 2) {
      _renderRecipientSource();
    }
    if (state.step === 3) {
      _syncQrOption();
      _syncComposePlaceholders();
      _renderLivePreview();
      _bindComposeLivePreview();
    }
    if (state.step === 4) {
      _renderReview();
      _renderSenderOptions();
    }

    // Buttons
    document.getElementById('campaignBackBtn').style.display = state.step > 1 ? '' : 'none';
    document.getElementById('campaignNextBtn').style.display = state.step < 4 ? '' : 'none';
    document.getElementById('campaignSendBtn').style.display = state.step === 4 ? '' : 'none';
  }

  // The hint text in the empty Subject / Message boxes fits the journey (task
  // #1114): an update to registered guests was offered "You're invited: Q2
  // Annual Summit" / "We'd like to invite you to...".
  function _syncComposePlaceholders() {
    const subjectEl = document.getElementById('campaignSubject');
    const bodyEl = document.getElementById('campaignBody');
    if (!subjectEl || !bodyEl) return;
    const event = state.events.find(e => e.id === state.event);
    const title = (event && event.title) || 'Q2 Annual Summit';
    if (_isRealEventSelected() && state.recipientSource === 'guests') {
      subjectEl.placeholder = `Important details for ${title}`;
      bodyEl.placeholder = `Hi {{first_name}},\n\nHere are the latest updates and details for ${title}...`;
    } else {
      subjectEl.placeholder = `You're invited: ${title}`;
      bodyEl.placeholder = "Hi {{first_name}},\n\nWe'd like to invite you to...";
    }
  }

  // Why Send came back to Compose, said on the page (task #1114): a missing
  // subject or message used to be a toast and a jump back, which read as the
  // Send button doing nothing. Each box that is empty is outlined, and the
  // outline goes as soon as it is typed into.
  function _showComposeError(noSubject, noBody) {
    const box = document.getElementById('campaignComposeError');
    const msg = noSubject && noBody
      ? 'Please enter both a subject line and message body before sending.'
      : noSubject
        ? 'Please enter a subject line for your campaign.'
        : 'Please enter a message body for your campaign.';
    if (box) { box.textContent = msg; box.style.display = ''; }
    [['campaignSubject', noSubject], ['campaignBody', noBody]].forEach(([id, bad]) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.classList.toggle('is-invalid', !!bad);
      if (bad && !el.dataset.invalidBound) {
        el.dataset.invalidBound = '1';
        el.addEventListener('input', () => {
          if (!el.value.trim()) return;
          el.classList.remove('is-invalid');
          if (!document.querySelector('#campaignSubject.is-invalid, #campaignBody.is-invalid')) _clearComposeError();
        });
      }
    });
    const first = document.getElementById(noSubject ? 'campaignSubject' : 'campaignBody');
    if (first) first.focus();
  }

  // Same for Recipients (task #1114 round 2): Send with nobody picked jumped
  // back to step 2 with only a toast. The message goes once someone is picked.
  function _showRecipientsError(msg) {
    const box = document.getElementById('campaignRecipientsError');
    if (!box) return;
    box.textContent = msg;
    box.style.display = '';
    box.scrollIntoView({ block: 'nearest' });
  }

  function _clearRecipientsError() {
    const box = document.getElementById('campaignRecipientsError');
    if (box) { box.textContent = ''; box.style.display = 'none'; }
  }

  function _clearComposeError() {
    const box = document.getElementById('campaignComposeError');
    if (box) { box.textContent = ''; box.style.display = 'none'; }
    ['campaignSubject', 'campaignBody'].forEach((id) => document.getElementById(id)?.classList.remove('is-invalid'));
  }

  // The opt-in tick, when Send is pressed without it (task #1109): a red
  // message under it and the row tinted, cleared as soon as it is ticked.
  function _setConsentError(on) {
    const row = document.getElementById('campaignConsentRow');
    const msg = document.getElementById('campaignConsentError');
    const cb = document.getElementById('campaignConsentCheckbox');
    if (row) row.classList.toggle('is-invalid', !!on);
    if (msg) msg.style.display = on ? '' : 'none';
    if (cb) {
      cb.setAttribute('aria-invalid', on ? 'true' : 'false');
      if (!cb.dataset.consentBound) {
        cb.dataset.consentBound = '1';
        cb.addEventListener('change', () => { if (cb.checked) _setConsentError(false); });
      }
    }
  }

  function _resetSaveDraftBtn() {
    const btn = document.getElementById('campaignSaveDraftBtn');
    if (!btn) return;
    btn.disabled = false;
    btn.classList.remove('is-saved');
    btn.innerHTML = '<i class="bi bi-bookmark"></i><span>Save draft</span>';
  }

  function _syncComposeFromDom() {
    const subjectEl = document.getElementById('campaignSubject');
    const bodyEl = document.getElementById('campaignBody');
    if (subjectEl) state.subject = subjectEl.value.trim();
    if (bodyEl) state.body = bodyEl.value.trim();
  }

  function _bindComposeLivePreview() {
    ['campaignSubject', 'campaignBody'].forEach(id => {
      const el = document.getElementById(id);
      if (!el || el.dataset.previewBound) return;
      el.dataset.previewBound = '1';
      el.addEventListener('input', _renderLivePreview);
    });
    _bindMergeFieldChips();
  }

  let _lastComposeFocus = 'campaignBody';
  function _bindMergeFieldChips() {
    ['campaignSubject', 'campaignBody'].forEach(id => {
      const el = document.getElementById(id);
      if (!el || el.dataset.focusBound) return;
      el.dataset.focusBound = '1';
      el.addEventListener('focus', () => { _lastComposeFocus = id; });
    });
    document.querySelectorAll('.merge-field-chip').forEach(chip => {
      if (chip.dataset.bound) return;
      chip.dataset.bound = '1';
      chip.addEventListener('click', () => {
        const token = chip.dataset.merge || chip.textContent;
        const target = document.getElementById(_lastComposeFocus) || document.getElementById('campaignBody');
        if (!target) return;
        const start = target.selectionStart ?? target.value.length;
        const end = target.selectionEnd ?? target.value.length;
        const before = target.value.slice(0, start);
        const after = target.value.slice(end);
        target.value = before + token + after;
        const caret = start + token.length;
        target.focus();
        target.setSelectionRange(caret, caret);
        target.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });
  }

  function goToStep(n) {
    if (!Number.isFinite(n) || n < 1 || n > 4) return;
    _syncComposeFromDom();
    state.step = n;
    _renderStep();
  }

  function next() {
    _syncComposeFromDom();
    if (state.step < 4) { state.step++; _renderStep(); }
  }

  function back() {
    _syncComposeFromDom();
    // In the event-specific flow, "Back" from the first real step returns to the
    // chooser rather than the generic event list (which we deliberately bypassed).
    if (state.chooser && state.step === 2) {
      _showChooser(state.chooserEventName);
      return;
    }
    if (state.step > 1) { state.step--; _renderStep(); }
  }

  // Show the event-specific chooser, hiding the step indicators, panes and the
  // advance buttons until the host picks "Invite to RSVP" or "Event Updates".
  function _showChooser(eventName) {
    const chooser = document.getElementById('campaignChooser');
    if (!chooser) return;
    const nameEl = document.getElementById('campaignChooserEventName');
    if (nameEl) nameEl.textContent = eventName || 'this event';
    const steps = document.querySelector('.campaign-steps');
    if (steps) steps.style.display = 'none';
    document.querySelectorAll('.campaign-pane').forEach(el => el.classList.remove('active'));
    chooser.style.display = '';
    ['campaignBackBtn', 'campaignNextBtn', 'campaignSendBtn', 'campaignSaveDraftBtn'].forEach(id => {
      const b = document.getElementById(id);
      if (b) b.style.display = 'none';
    });
  }

  function _hideChooser() {
    const chooser = document.getElementById('campaignChooser');
    if (chooser) chooser.style.display = 'none';
    const steps = document.querySelector('.campaign-steps');
    if (steps) steps.style.display = '';
    const sd = document.getElementById('campaignSaveDraftBtn');
    if (sd) sd.style.display = '';
  }

  // Pick a journey from the event-specific chooser.
  //  • 'invite'  → invitations / RSVP reminders to the contact list.
  //  • 'updates' → logistics / day-before reminder to registered guests, with the
  //                personal check-in QR-code attachment available at the Send step.
  function chooseMode(mode) {
    _hideChooser();
    state.recipientSource = (mode === 'updates') ? 'guests' : 'contacts';
    // Jump straight to Recipients — the event is already fixed, so the generic
    // event-selection step is skipped entirely.
    state.step = 2;
    _renderStep();
    _renderRecipientSource();
  }

  // Save draft says so on the button, then closes the window (task #1112);
  // the draft waits on the event's Outreach tab with Continue draft. A save
  // that only reached this browser stays open and says why.
  async function saveDraft() {
    const btn = document.getElementById('campaignSaveDraftBtn');
    if (btn) btn.disabled = true;
    const ok = await _saveDraft(true);
    if (!ok) { _resetSaveDraftBtn(); return; }
    if (btn) {
      btn.classList.add('is-saved');
      btn.innerHTML = '<i class="bi bi-check-lg"></i><span>Draft saved</span>';
    }
    setTimeout(() => {
      const modalEl = document.getElementById('orgCampaignModal');
      if (modalEl && modalEl.classList.contains('show')) bootstrap.Modal.getInstance(modalEl)?.hide();
    }, 1000);
  }

  function _formatSaved(iso) {
    try {
      const d = typeof iso === 'number' ? new Date(iso) : new Date(iso);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `Last saved ${hh}:${mm}`;
    } catch { return ''; }
  }

  async function send() {
    if (state.sending) return;

    _syncComposeFromDom();

    const useGuests = _isRealEventSelected() && state.recipientSource === 'guests';

    // Full validation at send time (previously gated per-step)
    if (useGuests) {
      if (!state.eventGuests || state.eventGuests.length === 0) {
        goToStep(2);
        _showRecipientsError('This event has no registered guests with an email yet, so there is no one to send this to.');
        return;
      }
      if (state.selectedGuests.size === 0) {
        goToStep(2);
        _showRecipientsError('Please pick at least one registered guest to send your campaign to.');
        return;
      }
    } else if (state.selectedContacts.size === 0) {
      goToStep(2);
      _showRecipientsError('Please pick at least one recipient for your campaign.');
      return;
    }
    if (!state.subject || !state.body) {
      goToStep(3);
      _showComposeError(!state.subject, !state.body);
      return;
    }
    // The QR switch follows the journey even if Compose was skipped over with
    // the step buttons (task #1114).
    _syncQrOption();

    const consent = document.getElementById('campaignConsentCheckbox').checked;
    if (!consent) {
      _setConsentError(true);
      return;
    }
    _setConsentError(false);

    if (!useGuests) {
      const recipients = state.contacts.filter(c => state.selectedContacts.has(c.id));
      if (recipients.length === 0) { _showToast('No recipients', 'warning'); return; }
    }

    state.sending = true;
    const btn = document.getElementById('campaignSendBtn');
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Sending...';

    try {
      const res = await window.OrgContext.orgFetch('/campaigns/send', {
        method: 'POST',
        body: JSON.stringify({
          eventId: state.event,
          subject: state.subject,
          body: state.body,
          senderPref: state.senderPref,
          // Guests mode: the server resolves the authoritative guest list from
          // the event's responses — no contact ids exist for registrants.
          recipientSource: useGuests ? 'guests' : 'contacts',
          recipientIds: useGuests ? [] : [...state.selectedContacts],
          // Which of the event's registered guests to send to (task #626).
          // The server still resolves every recipient from the event's own
          // responses — this only narrows that list, it never adds to it.
          guestEmails: useGuests ? [...state.selectedGuests] : undefined,
          consentAcknowledged: true,
          includeQRCode: !!state.includeQRCode && !!state.event && state.event !== '__events-page__'
        })
      });

      if (res.success && (res.sent > 0 || res.failed === 0)) {
        await _clearDraft();
        const parts = [`${res.sent} delivered`];
        if (res.failed) parts.push(`${res.failed} failed`);
        if (res.skipped) parts.push(`${res.skipped} skipped (unsubscribed)`);
        _showToast(`Campaign sent: ${parts.join(', ')}`);
        bootstrap.Modal.getInstance(document.getElementById('orgCampaignModal'))?.hide();
      } else if (res.aborted || (Array.isArray(res.errors) && res.errors.length)) {
        const firstErr = res.errors?.[0]?.error || res.error || 'unknown error';
        _showToast(`Send stopped after ${res.sent || 0} delivered: ${firstErr}`, 'danger');
      } else {
        _showToast('Send failed: ' + (res.error || 'unknown error'), 'danger');
      }
    } catch (err) {
      _showToast('Send error: ' + err.message, 'danger');
    } finally {
      state.sending = false;
      btn.disabled = false;
      btn.innerHTML = '<i class="bi bi-send"></i><span>Send now</span>';
    }
  }

  // Send a one-off test copy of the campaign email to the logged-in host only,
  // so they can see exactly how it lands in a real inbox before sending to the
  // list. Reuses the corporate /send-test endpoint (skipCredit) and the same
  // rendered email body the recipients would get.
  async function sendTest() {
    // The button now sits ON the Compose step (task #627), where subject and
    // body are still only in the DOM — state is otherwise synced on step
    // changes, so without this the test would send the last SAVED draft, or
    // nothing at all on a first pass.
    _syncComposeFromDom();
    const focusCompose = (id) => {
      if (state.step !== 3) { goToStep(3); }
      const el = document.getElementById(id);
      if (el) { el.focus(); el.scrollIntoView({ block: 'center' }); }
    };
    if (!state.subject) { _showToast('Add a subject line first', 'warning'); focusCompose('campaignSubject'); return; }
    if (!state.body) { _showToast('Write your message first', 'warning'); focusCompose('campaignBody'); return; }
    const myEmail = (window.firebaseAuth?.currentUser?.email || '').trim();
    if (!myEmail) { _showToast('Could not determine your email address — please sign in again', 'warning'); return; }

    const btn = document.getElementById('campaignTestEmailBtn');
    const statusEl = document.getElementById('campaignTestEmailStatus');
    const setStatus = (msg, cls) => { if (statusEl) { statusEl.textContent = msg; statusEl.className = 'small mt-2 ' + (cls || ''); } };

    const myName = window.firebaseAuth?.currentUser?.displayName || myEmail.split('@')[0];
    const html = _renderEmailBody({ name: myName, email: myEmail }, true);

    if (btn) btn.disabled = true;
    setStatus('Sending test email…', 'text-body-secondary');
    try {
      const res = await window.OrgContext.orgFetch('/send-test', {
        method: 'POST',
        body: JSON.stringify({
          recipient: { email: myEmail, name: myName },
          subject: state.subject,
          body: html,
          senderPref: state.senderPref
        })
      });
      if (res && res.success) {
        setStatus('Test email sent to ' + myEmail, 'text-success');
        _showToast('Test email sent to ' + myEmail);
      } else {
        setStatus((res && res.error) || 'Could not send test email', 'text-danger');
      }
    } catch (err) {
      setStatus('Could not send test email: ' + err.message, 'text-danger');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  // For the Outreach tab (tasks #1112, #1115): the saved draft for its row, and
  // a sent campaign drawn the way it was sent, with a sample recipient.
  function previewHtml({ event, body, recipientSource, includeQRCode }) {
    return _renderEmailBody({ name: 'Sample Recipient', email: 'sample@example.com' }, false, {
      body, event: event || null, isEventsPage: false,
      useGuests: !!event && recipientSource === 'guests', includeQRCode: !!includeQRCode
    });
  }

  return { open, next, back, send, sendTest, toggleAll, toggleAllGuests, goToStep, saveDraft, chooseMode,
    getDraft: _loadDraft, discardDraft: _clearDraft, previewHtml };
})();
